import { useState } from "react";
import { motion } from "framer-motion";
import { Radar, PenTool, CalendarClock, PhoneCall, CalendarCheck, LayoutDashboard } from "lucide-react";

const POLES = [
  {
    id: "veille",
    num: "01",
    icon: Radar,
    title: "Veille",
    desc: "Concurrents sur Instagram, TikTok, Facebook et LinkedIn. Score viral (posts qui font ×5 à ×20 la normale), palmarès du jour, veille des pubs concurrentes, tendances.",
  },
  {
    id: "creation",
    num: "02",
    icon: PenTool,
    title: "Création",
    desc: "Brand kit, voix de la marque qui apprend, scripts seconde par seconde, contenu décliné sur tous les formats, vidéos UGC IA, influenceur/mascotte IA de marque, pubs statiques et packshots, vidéos cinématiques.",
  },
  {
    id: "diffusion",
    num: "03",
    icon: CalendarClock,
    title: "Diffusion",
    desc: "Calendrier éditorial, validation client en 1 clic, publication programmée, test de pubs en volume.",
  },
  {
    id: "capture",
    num: "04",
    icon: PhoneCall,
    title: "Capture & réponse",
    desc: "Réponse instantanée aux leads, appel manqué → SMS automatique, chatbot sur le site, agent vocal 24/7.",
  },
  {
    id: "conversion",
    num: "05",
    icon: CalendarCheck,
    title: "Conversion",
    desc: "Prise de RDV avec rappels, relance des anciens clients et des devis non signés, avis Google automatiques.",
  },
  {
    id: "pilotage",
    num: "06",
    icon: LayoutDashboard,
    title: "Pilotage",
    desc: "Tableau de bord, rapport mensuel + débrief humain, chat avec Giulia (« fais-moi 10 pubs pour Noël »).",
  },
];

const C = 160;
const R = 112;

const nodePos = (i) => {
  const a = ((-90 + i * 60) * Math.PI) / 180;
  return { x: C + R * Math.cos(a), y: C + R * Math.sin(a) };
};

const arcPath = (i) => {
  const a1 = ((-90 + i * 60 + 18) * Math.PI) / 180;
  const a2 = ((-90 + ((i + 1) % 6) * 60 - 18) * Math.PI) / 180;
  const x1 = C + R * Math.cos(a1);
  const y1 = C + R * Math.sin(a1);
  const x2 = C + R * Math.cos(a2);
  const y2 = C + R * Math.sin(a2);
  return `M ${x1.toFixed(1)} ${y1.toFixed(1)} A ${R} ${R} 0 0 1 ${x2.toFixed(1)} ${y2.toFixed(1)}`;
};

const CycleDiagram = ({ active, setActive }) => (
  <div className="relative mx-auto w-full max-w-[360px]" data-testid="cycle-diagram">
    <svg viewBox="0 0 320 320" className="w-full">
      <defs>
        <linearGradient id="cycle-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#818CF8" stopOpacity="0.7" />
          <stop offset="1" stopColor="#8B5CF6" stopOpacity="0.7" />
        </linearGradient>
      </defs>
      <circle
        cx={C}
        cy={C}
        r={140}
        fill="none"
        stroke="#8B5CF6"
        strokeOpacity="0.18"
        strokeWidth="1"
        strokeDasharray="2 10"
        className="animate-spinSlow"
        style={{ transformBox: "view-box", transformOrigin: "center" }}
      />
      {POLES.map((_, i) => (
        <path
          key={`arc-${i}`}
          d={arcPath(i)}
          fill="none"
          stroke="url(#cycle-grad)"
          strokeWidth="1.5"
          strokeDasharray="5 7"
          className="transition-all duration-300"
          opacity={active === null || active === i ? 0.9 : 0.25}
        />
      ))}
      {POLES.map((p, i) => {
        const { x, y } = nodePos(i);
        const isActive = active === i;
        return (
          <g
            key={p.id}
            onMouseEnter={() => setActive(i)}
            onMouseLeave={() => setActive(null)}
            className="cursor-pointer"
            data-testid={`cycle-node-${p.id}`}
          >
            <circle
              cx={x}
              cy={y}
              r={isActive ? 26 : 22}
              fill={isActive ? "#8B5CF6" : "#161030"}
              stroke="#8B5CF6"
              strokeOpacity={isActive ? 1 : 0.45}
              strokeWidth="1.5"
              className="transition-all duration-300"
            />
            <text
              x={x}
              y={y + 4}
              textAnchor="middle"
              fill={isActive ? "#0B0B1A" : "#C4B5FD"}
              fontSize="12"
              fontFamily="'JetBrains Mono', monospace"
              className="transition-all duration-300"
            >
              {p.num}
            </text>
          </g>
        );
      })}
    </svg>
    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
      <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-mist">Le cycle</span>
      <span className="font-display font-bold text-2xl text-grad mt-1">Giulia</span>
    </div>
  </div>
);

export const CycleSection = () => {
  const [active, setActive] = useState(null);

  return (
    <section id="cycle" className="relative py-24 sm:py-32 scroll-mt-20">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-2xl"
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-viol-soft mb-4">01 — Le cycle</p>
          <h2 className="font-display font-bold text-3xl sm:text-5xl text-white leading-tight">
            Six pôles. Une seule boucle.
          </h2>
          <p className="mt-5 text-mist leading-relaxed">
            Giulia n'est pas une liste d'outils : c'est un cycle complet, de la
            surveillance du marché jusqu'au rendez-vous dans votre agenda. Vous
            gardez le contrôle à chaque étape.
          </p>
        </motion.div>

        <div className="mt-14 grid lg:grid-cols-[0.85fr_1.15fr] gap-12 items-start">
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="lg:sticky lg:top-28"
          >
            <CycleDiagram active={active} setActive={setActive} />
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-4">
            {POLES.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.article
                  key={p.id}
                  data-testid={`cycle-card-${p.id}`}
                  onMouseEnter={() => setActive(i)}
                  onMouseLeave={() => setActive(null)}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.6, delay: (i % 2) * 0.08 + Math.floor(i / 2) * 0.08, ease: [0.16, 1, 0.3, 1] }}
                  className={`h-full rounded-2xl border bg-ink-surface/70 p-6 transition-all duration-300 hover:-translate-y-1 ${
                    active === i
                      ? "border-viol/50 shadow-[0_0_32px_rgba(139,92,246,0.18)]"
                      : "border-ink-line"
                  }`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-viol/10 border border-viol/25 text-viol-soft">
                      <Icon size={19} />
                    </span>
                    <span className="font-mono text-xs text-viol/70">{p.num}</span>
                  </div>
                  <h3 className="font-display font-bold text-lg text-white">{p.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-mist">{p.desc}</p>
                </motion.article>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CycleSection;
